import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const FEATURES = [
  {
    title: 'Analysez votre courrier',
    subtitle: 'Collez ou scannez votre courrier administratif. L\'IA identifie le probleme et vous explique la situation.',
    image: 'https://images.unsplash.com/photo-1648279220423-14606cb98f22?w=800&q=80&auto=format',
    screen: 'dashboard',
    link: '/register'
  },
  {
    title: 'Reponse officielle en 30s',
    subtitle: 'Un courrier professionnel genere avec les bons articles de loi, le bon formalisme et le bon destinataire.',
    image: 'https://images.unsplash.com/photo-1637942695886-69c5094fb1bb?w=800&q=80&auto=format',
    screen: 'letter',
    link: '/register'
  },
  {
    title: 'Chatbot administratif',
    subtitle: 'Posez votre question. Kadmi collecte vos informations et redige le courrier pour vous.',
    image: 'https://images.pexels.com/photos/9841340/pexels-photo-9841340.jpeg?auto=compress&w=800',
    screen: 'chatbot',
    link: '/register'
  },
  {
    title: 'Export PDF professionnel',
    subtitle: 'Telechargez, partagez par WhatsApp, email ou SMS. Pret a envoyer en un clic.',
    image: 'https://images.unsplash.com/photo-1689421754256-cfde43e8b81e?w=800&q=80&auto=format',
    screen: 'pdf',
    link: '/register'
  }
];

const SCREEN_MOCKUPS = {
  dashboard: (
    <div className="w-full rounded-xl overflow-hidden shadow-2xl" style={{ background: '#0F0F1A', border: '1px solid #2E2E45' }}>
      <div className="flex items-center gap-1 px-3 py-1.5" style={{ background: '#1A1A2E' }}>
        <div className="w-2 h-2 rounded-full bg-red-400" />
        <div className="w-2 h-2 rounded-full bg-yellow-400" />
        <div className="w-2 h-2 rounded-full bg-green-400" />
        <span className="text-[8px] text-gray-500 ml-2">kadmi-ai.com</span>
      </div>
      <div className="p-3 space-y-2">
        <p className="text-[9px] text-gray-400">Quel est votre probleme ?</p>
        <div className="grid grid-cols-2 gap-1.5">
          {['CAF', 'Prefecture', 'Amende', 'Impots'].map(c => (
            <div key={c} className="rounded-lg px-2 py-1.5 text-[8px] font-medium text-white" style={{ background: '#252538', border: '1px solid #2E2E45' }}>{c}</div>
          ))}
        </div>
        <div className="rounded-lg p-2 mt-1" style={{ background: '#252538' }}>
          <p className="text-[8px] text-gray-400">Decrivez votre situation...</p>
        </div>
      </div>
    </div>
  ),
  letter: (
    <div className="w-full rounded-xl overflow-hidden shadow-2xl" style={{ background: '#0F0F1A', border: '1px solid #2E2E45' }}>
      <div className="flex items-center gap-1 px-3 py-1.5" style={{ background: '#1A1A2E' }}>
        <div className="w-2 h-2 rounded-full bg-red-400" />
        <div className="w-2 h-2 rounded-full bg-yellow-400" />
        <div className="w-2 h-2 rounded-full bg-green-400" />
      </div>
      <div className="p-3 space-y-1.5">
        <div className="flex items-center gap-1.5 mb-1">
          <div className="w-4 h-4 rounded bg-green-500/20 flex items-center justify-center"><span className="text-[7px] text-green-400">OK</span></div>
          <p className="text-[9px] text-green-400 font-medium">Courrier genere</p>
        </div>
        <div className="space-y-1 text-[7px] text-gray-300 leading-relaxed">
          <p>Objet : Contestation suspension APL</p>
          <p>Madame, Monsieur,</p>
          <p>Par la presente, je conteste la decision de suspension de mon APL notifiee le...</p>
          <p className="text-purple-400">Art. L.831-1 du CSS</p>
        </div>
      </div>
    </div>
  ),
  chatbot: (
    <div className="w-full rounded-xl overflow-hidden shadow-2xl" style={{ background: '#0F0F1A', border: '1px solid #2E2E45' }}>
      <div className="flex items-center gap-1 px-3 py-1.5" style={{ background: '#7FB342' }}>
        <span className="text-[9px] text-white font-medium">Kadmi</span>
      </div>
      <div className="p-3 space-y-2">
        <div className="rounded-lg px-2 py-1.5 text-[8px] text-white max-w-[80%]" style={{ background: '#252538' }}>
          La CAF a coupe mon RSA
        </div>
        <div className="rounded-lg px-2 py-1.5 text-[8px] text-white max-w-[85%] ml-auto" style={{ background: '#7FB342' }}>
          Je comprends. Quel est votre numero d'allocataire CAF ?
        </div>
        <div className="rounded-lg px-2 py-1.5 text-[8px] text-white max-w-[70%]" style={{ background: '#252538' }}>
          1234567A
        </div>
        <div className="rounded-lg px-2 py-1.5 text-[8px] text-white max-w-[85%] ml-auto" style={{ background: '#7FB342' }}>
          Merci. Je genere votre courrier...
        </div>
      </div>
    </div>
  ),
  pdf: (
    <div className="w-full rounded-xl overflow-hidden shadow-2xl" style={{ background: '#0F0F1A', border: '1px solid #2E2E45' }}>
      <div className="flex items-center gap-1 px-3 py-1.5" style={{ background: '#1A1A2E' }}>
        <div className="w-2 h-2 rounded-full bg-red-400" />
        <div className="w-2 h-2 rounded-full bg-yellow-400" />
        <div className="w-2 h-2 rounded-full bg-green-400" />
      </div>
      <div className="p-3 space-y-2">
        <div className="flex gap-1.5">
          <div className="flex-1 rounded-lg py-1.5 text-center text-[8px] text-white font-medium" style={{ background: '#7FB342' }}>PDF</div>
          <div className="flex-1 rounded-lg py-1.5 text-center text-[8px] text-gray-300" style={{ background: '#252538' }}>WhatsApp</div>
          <div className="flex-1 rounded-lg py-1.5 text-center text-[8px] text-gray-300" style={{ background: '#252538' }}>Email</div>
        </div>
        <div className="rounded-lg p-2 text-center" style={{ background: '#252538' }}>
          <div className="w-8 h-10 mx-auto mb-1 rounded bg-white/10 flex items-center justify-center">
            <span className="text-[10px] text-red-400 font-bold">PDF</span>
          </div>
          <p className="text-[8px] text-gray-400">courrier_caf_2026.pdf</p>
          <p className="text-[7px] text-green-400 mt-0.5">Pret a telecharger</p>
        </div>
      </div>
    </div>
  )
};

const FeatureShowcase = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section className="py-16 px-4" data-testid="feature-showcase">
      <div className="max-w-5xl mx-auto">
        <h2 className="font-heading text-2xl sm:text-3xl text-center mb-3" style={{ color: isDark ? '#F0EFFF' : '#1A1A2E' }}>
          Comment ca marche
        </h2>
        <p className="text-center text-sm mb-10" style={{ color: isDark ? '#9998B8' : '#6B6B8A' }}>
          De votre probleme a votre courrier officiel, en 4 etapes
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FEATURES.map((f, i) => (
            <Link to={f.link} key={i} className="group block" data-testid={`feature-card-${i}`}>
              <div
                className="relative rounded-2xl overflow-hidden transition-transform duration-300 group-hover:-translate-y-1"
                style={{ minHeight: 380 }}
              >
                {/* Background image */}
                <img
                  src={f.image}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Dark overlay */}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.75) 100%)' }} />

                {/* Content */}
                <div className="relative z-10 p-5 flex flex-col h-full" style={{ minHeight: 380 }}>
                  {/* Title + arrow */}
                  <div className="flex items-start justify-between mb-1.5">
                    <h3 className="text-lg font-bold text-white">{f.title}</h3>
                    <div className="w-8 h-8 rounded-full bg-white/10 backdrop-blur flex items-center justify-center shrink-0 group-hover:bg-white/20 transition-colors">
                      <ArrowUpRight className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <p className="text-sm text-white/70 mb-auto leading-relaxed">{f.subtitle}</p>

                  {/* Phone mockup with screen */}
                  <div className="mt-4 mx-auto w-[70%] max-w-[220px]">
                    {/* Phone frame */}
                    <div className="rounded-2xl overflow-hidden p-1" style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                      {/* Notch */}
                      <div className="w-16 h-1 mx-auto rounded-full bg-black/40 mb-1" />
                      {SCREEN_MOCKUPS[f.screen]}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcase;
